import {
  Action,
  trackUploadProgress,
  receiveBadUpload,
  receiveBadUploadHistoryAction,
  clearBadUpload,
  updateFormState,
  updateFormMetadata,
} from '../Actions/UserDatasetUploadActions';

import { UserDatasetUpload } from '../Utils/types';
import {
  PartialDatasetDetails,
  DatasetUploads,
  ValidationErrors,
} from '../Service';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import { StateSlice } from './types';
import { defaultDatasetDetails } from '../Service/Model/constructors';

export const key = 'userDatasetUpload';

/**
 * Upload form fields and flags that are only relevant to the client application
 * and are not used by any other user dataset features or functionality.
 */
export interface ClientSideUploadFormState {
  readonly isStudy: boolean | undefined;
  readonly hasExternalSources: boolean | undefined;
  readonly hasDisclaimer: boolean | undefined;
  readonly hasExperimentalOrganism: boolean | undefined;
  readonly hasPublications: boolean | undefined;
}

export function defaultClientSideUploadFormState(): ClientSideUploadFormState {
  return {
    isStudy: undefined,
    hasExternalSources: undefined,
    hasDisclaimer: undefined,
    hasExperimentalOrganism: undefined,
    hasPublications: undefined,
  };
}

export interface DatasetFormState {
  readonly datasetDetails: PartialDatasetDetails;
  readonly fileUploads: DatasetUploads;
  readonly formMetaState: ClientSideUploadFormState;
}

export const DefaultDatasetFormState: DatasetFormState = {
  datasetDetails: defaultDatasetDetails(),
  fileUploads: {},
  formMetaState: defaultClientSideUploadFormState(),
};

export function useDatasetFormState(): DatasetFormState {
  return (
    useSelector(
      (state: StateSlice) => state.userDatasetUpload.formState,
      isEqual
    ) ?? DefaultDatasetFormState
  );
}

export interface State {
  readonly formState?: DatasetFormState;
  readonly uploads?: Array<UserDatasetUpload>;
  readonly badUploadMessages?: BadUpload[];
  readonly badAllUploadsActionMessage?: { message: string; timestamp: number };
  readonly uploadProgress?: { progress: number | null };
}

export type BadUpload =
  | { type: 400; message: string }
  | { type: 422; errors: ValidationErrors }
  | { type: 500; message: string };

export function reduce(state: State = {}, action: Action): State {
  switch (action.type) {
    case receiveBadUpload.type:
      return { ...state, badUploadMessages: action.payload };
    case clearBadUpload.type:
      return { ...state, badUploadMessages: undefined };
    case trackUploadProgress.type:
      return { ...state, uploadProgress: action.payload };
    case receiveBadUploadHistoryAction.type:
      return { ...state, badAllUploadsActionMessage: action.payload };
    case updateFormMetadata.type:
      return {
        ...state,
        formState: {
          ...state.formState!,
          datasetDetails: action.payload,
        },
      };
    case updateFormState.type:
      return { ...state, formState: action.payload };
    default:
      return state;
  }
}
